import React, { useState, useEffect, useRef, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { formattedTime } from '@/utils/date'
import WaveSurfer from 'wavesurfer.js'

import { ChevronDownIcon, ChevronUpIcon, ArrowsExpandIcon } from '@heroicons/react/outline'
import { SVGIcon } from '@/components/icon'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'

// http://wavesurfer-js.org/projects/
// Truly awesome stuff that could be done with audio editing right from the browser.
// Possible to record, cut, mix, annotate, straight from browser
// Possible to play file immediately, before even uplaoding it too, Look in imageuploadform URL.createObjectURL
//
// Possible to preload next song in the queue: https://wavesurfer-js.org/docs/methods.html

const formWaveSurferOptions = ref => ({
  container: ref,
  waveColor: '#eee',
  progressColor: '#f7784e',
  cursorColor: '#f7784e',
  barWidth: 3,
  barRadius: 3,
  responsive: true,
  height: 60,
  normalize: true, // If true, normalize by the maximum peak instead of 1.0.
  partialRender: true // Use the PeakCache to improve rendering speed of large waveforms.
})

const nullState = {
  objectName: null,
  userDisplayName: null,
  userUrl: null,
  imageUrl: null,
  objectUrl: null
}

const FooterPlayer = () => {
  const [isOpen, setIsOpen] = useState(false)
  // Pages with something pinned to the bottom of the screen (the DM composer)
  // read this to step above the open player bar instead of hiding under it.
  useEffect(() => {
    document.body.classList.toggle('footer-player-open', isOpen)
  }, [isOpen])
  const { putpostRequest } = useQuery()
  const [mediaObject] = useGlobalState('mediaObject')
  const [playing, setPlaying] = useGlobalState('playing')
  const [loadingMedia, setLoadingMedia] = useGlobalState('loadingMedia')

  const waveformRef = useRef(null)
  const wavesurfer = useRef(null)
  const [duration, setDuration] = useState(0.0)
  const [currentPosition, setCurrentPosition] = useState(0.0)

  const [state, setState] = useReducer(
    (state, newState) => (
      { ...state, ...newState }), nullState
    )
  const { objectName, userUrl, objectUrl, userDisplayName, imageUrl } = state

  const { mediaUrl } = mediaObject

  // create new WaveSurfer instance
  // On component mount and when url changes
  useEffect(() => {
    if (!mediaObject?.objectId) { return }

    setPlaying(false)
    setLoadingMedia(true)

    const data = {
      object_id: mediaObject.objectId,
      object_kind: mediaObject.objectKind
    }

    putpostRequest('/api/v3/media_objects', 'POST', data, (err, jsonData) => {
      if (err) { /* hook */ return }

      setState({
        objectName: jsonData.objectName,
        userDisplayName: jsonData.userDisplayName,
        userUrl: jsonData.userUrl,
        imageUrl: jsonData.imageUrl,
        objectUrl: jsonData.objectUrl
      })
    })

    const options = formWaveSurferOptions(waveformRef.current)
    wavesurfer.current = WaveSurfer.create(options)

    if (mediaUrl) {
      wavesurfer.current.load(mediaUrl)
    }

    wavesurfer.current.on('ready', function() {
      // https://wavesurfer-js.org/docs/methods.html
      // make sure object stillavailable when file loaded
      if (wavesurfer.current) {
        wavesurfer.current.play()
        setPlaying(true)
        setIsOpen(true)
        setLoadingMedia(false)
        wavesurfer.current.setVolume(1)
        setDuration(formattedTime(wavesurfer.current.getDuration()))
      }
    })

    wavesurfer.current.on('audioprocess', function() {
      if (wavesurfer.current) {
        const time = wavesurfer.current.getCurrentTime()
        const fTime = formattedTime(time)
        if (fTime !== currentPosition) {
          setCurrentPosition(fTime)
        }
      }
    })

    // Removes events, elements and disconnects Web Audio nodes.
    // when component unmount
    return () => wavesurfer.current.destroy()
  }, [mediaObject])

  useEffect(() => {
    if (!wavesurfer.current) { return }

    if (playing && wavesurfer.current.isPlaying()) { return }
    if (!playing && !wavesurfer.current.isPlaying()) { return }

    wavesurfer.current.playPause()
  }, [playing])

  const handlePlayPause = () => {
    setPlaying(!playing)
    wavesurfer.current.playPause()
  }

  // always need to have the wavesurfer id in the dom otherwise madness
  return <>
    <div onClick={() => setIsOpen(!isOpen)} className={`fixed inset-x-auto right-3 sm:right-6 lg:right-8 ${isOpen ? 'bottom-16' : 'bottom-0'} w-8 h-8 bg-cccblue dark:bg-cccpurple text-white cursor-pointer rounded-t-lg z-10`}>
      <span className="sr-only" hidden>Audio Player</span>
      { isOpen && <ChevronDownIcon className='h-8 w-8 text-white' /> }
      { !isOpen && <ChevronUpIcon className='h-8 w-8 text-white' /> }
    </div>

    <div className={`fixed inset-x-0 bottom-0 z-10 bg-cccblue dark:bg-cccpurple text-white ${isOpen ? 'h-16' : 'max-h-0'} `}>
      <div className="w-full mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-center space-x-4 sm:space-x-0">
            { !objectUrl && !loadingMedia && <div className='flex items-center justify-center text-xs sm:text-sm'>
              Nothing is in the playlist. Click on one of those
                <div className="mx-1 sm:mx-4 h-11 bg-cccorange rounded-full p-1">
                  <SVGIcon name='play' width='37' height='37' autoDark/>
                </div>
              icons around the website to hear stuff.
            </div> }
            { imageUrl && <img className="hidden md:block h-8 w-8 rounded-full flex-none" src={imageUrl} alt={imageUrl} /> }
            { objectUrl && <>
              <div className='flex flex-col w-32 sm:w-72 sm:mx-6 overflow-hidden'>
                <Link to={objectUrl} className='text-white hover:text-gray-200 cursor-pointer text-sm truncate hover:underline'>{objectName}</Link>
                <Link to={userUrl} className='text-white hover:text-gray-200 cursor-pointer text-sm truncate hover:underline'>{userDisplayName}</Link>
              </div>
            </> }
            <div className={`${(!objectUrl && !loadingMedia) ? '' : 'sm:flex-1'} flex items-center justify-center`}>
              { objectUrl && <>
                { loadingMedia && <Loading noMessage noLoadingMessage /> }
                { !loadingMedia && <>
                  { !playing && <div onClick={handlePlayPause} className="h-11 bg-cccorange rounded-full p-1 cursor-pointer">
                    <SVGIcon name='play' width='37' height='37' autoDark/>
                  </div> }
                  { playing && <div onClick={handlePlayPause} className="h-11 bg-cccorange rounded-full p-1 cursor-pointer">
                    <SVGIcon name='pause' width='37' height='37' autoDark/>
                  </div> }
                </> }
              </> }
              <div className='flex-grow hidden sm:block ml-3 cursor-pointer'>
                <div id="waveform" className='' ref={waveformRef} />
              </div>
            </div>

            { objectUrl && <>
              <div className="flex ml-0 sm:ml-6 cursor-pointer gap-x-3">
                <div className='flex flex-col w-20'>
                  <span>{currentPosition}</span>
                  <span>{duration}</span>
                </div>
                { false && <>
                  <SVGIcon className='cursor-pointer' name='playlist' width='45' height='50' fill="#fff"/>
                  <span className="sr-only" hidden>Open Playlist</span>
                  <ArrowsExpandIcon className='h-12 w-12' />
                  <span className="sr-only" hidden>Expand details</span>
                </> }
              </div>
            </> }
      </div>
    </div>
  </>
}

export default FooterPlayer
